//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useMemo, useRef, useState } from 'react';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';
import {
  Dialog,
  InputBase,
} from '@mui/material';
import {
  Delete,
} from '@mui/icons-material';

// === COMPONENTS ===
import Styled from 'components/Styled/Styled';
import { Spacer } from 'components/Spacer';

// === HOOKS ===
import { useLayout } from '../Panel/PanelContext';

//#endregion

//-----------------------------------------------------------------
export function LayoutPresets( props ) {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const { is_open } = props;

  const theme = useTheme();

  const { layouts, saveLayout, removeLayout } = useLayout();

  const [ selected_preset, setSelectedPreset ] = useState(null);
  const [ error_message  , setErrorMessage   ] = useState(null);
  
  const rows = useMemo( () => {
    return [...layouts.keys()].map( name => { return { id: name, name } } );
  }, [layouts] );
  
  const ref_preset = useRef();

  //#endregion

  //#region [STYLES]

  const _flex_display = { display: 'flex' };

  const _bg_secondary =  { background: theme.palette.background.secondary[theme.palette.mode] };
  const _bg_pd        =  { background: theme.palette.primary[theme.palette.mode] };
  const _border_pd    =  { border:'2px solid '+theme.palette.primary[theme.palette.mode], borderRadius: '5px' };

  const DIALOG = {
    open           : is_open,
    fullWidth      : true,
    onClose        : handleClose,
    'aria-labelledby' : 'form-dialog-title'
  };

  //#endregion
    
  //#region [EFFECT]

  //#endregion
  
  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleClose() {
    //-----------------------------------------------------------------
    ref_preset.current.value = '';
    setSelectedPreset(null);
    if( error_message ) setErrorMessage('');

    props.onClose();
  };

  //-----------------------------------------------------------------
  function onClick_Preset( event ) {
    //-----------------------------------------------------------------
    ref_preset.current.value = event.target.innerText || '';
    setSelectedPreset( event.target.innerText );
    
    if( error_message ) setErrorMessage('');
  }

  //-----------------------------------------------------------------
  function onClick_Save( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();

    if( ref_preset.current.value === '' ) {
      setErrorMessage( 'Please enter or select a name.');
      return;
    }

    if( ['DEFAULT', 'CUSTOM (LOCAL)'].includes( ref_preset.current.value.toUpperCase() ) ) {
      setErrorMessage( 'Name is reserved. Please use another name.' );
      return;
    }

    saveLayout( ref_preset.current.value );

    handleClose();
  }

  //-----------------------------------------------------------------
  function onChange_Name( event ) {
    //-----------------------------------------------------------------
    if( selected_preset ) setSelectedPreset( null );

    const value = event.target.value.toUpperCase();
    if( ['DEFAULT', 'CUSTOM (LOCAL)'].includes( value ) ) {
      setErrorMessage( 'Name is reserved. Please use another name.' );
      return;
    }

    if( rows.find( row => row.name.toUpperCase() === value ) ) {
      setSelectedPreset( event.target.value );
    }

    ref_preset.current.value = event.target.value;
    
    if( error_message ) setErrorMessage('');
  }

  //-----------------------------------------------------------------
  function removePreset( name ) {
    //-----------------------------------------------------------------
    setSelectedPreset( null );
    ref_preset.current.value = '';

    removeLayout( name );
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const presets = rows.map( (row, index) => {
      const is_selected = selected_preset === row.name;
      const is_reserved = ['Default', 'Custom (local)'].includes( row.name )
      const cursor      = { cursor    : is_reserved ? '' : 'pointer' };
      const color       = { color     : is_reserved ? 'grey' : '' }
      const background  = (is_selected && !is_reserved) ? _bg_pd : _bg_secondary;
      const click_event = is_reserved ? null : onClick_Preset;
      const icon_delete = is_reserved ? <Spacer size={'20px'} /> : <Delete sx={{width: '20px', height: '20px'}} onClick={()=> removePreset(row.name)} />;

      return (
        <Styled key={row.name} styles={[_flex_display, { alignItems: 'center' }, {width: '', height: '40px'}, { fontSize: 18 }, cursor, background, color, { padding: '10px' } ]} onClick={click_event} >
          {icon_delete}
          <Spacer size={'10px'} />
          {row.name}
        </Styled>
      )
    });

    return (
      <Dialog { ...DIALOG } >
        <Styled styles={[ { width:'100%', height:'100%' }, { padding: '10px' }, { overflow: 'auto' } ]} >
          <Styled styles={[ { width:      '', height:  '30px' }, { fontSize: 20 } ] }>
            Presets:
          </Styled>
          <Styled styles={[ { width: '100%', height: '100%' }, { padding: '0px' }, { display: 'flex', flexDirection: 'column' }, _bg_secondary, _border_pd, { overflow: 'auto' } ]}>
            {presets}
          </Styled>
          <Spacer size={'10px'} />
          {displayPresetName()}
          <Styled styles={[{width: '', height: '20px'}]}>
            {error_message}
          </Styled>
          <Spacer size={'10px'} />
          <Styled styles={[_flex_display, { justifyContent: 'right' }]}>
            <Styled styles={[_flex_display, { width: '50px', height: '40px'}, { padding: '5px' }, { justifyContent: 'center', alignItems: 'center'}, {background: theme.palette.primary.main, color: theme.palette.primary.contrastText, borderRadius: '5px'}, {cursor: 'pointer'}]}
              onClick={onClick_Save}
            >
              Save
            </Styled>
          </Styled>
        </Styled>
      </Dialog>
    )
  }

  //-----------------------------------------------------------------
  function displayPresetName() {
    //-----------------------------------------------------------------
    return (
      <Styled styles={[{width:'100%', height:'fit-content'}]} >
        <Styled styles={[{width: '', height: '30px'}, { fontSize: 20 } ]}>New preset:</Styled>
        <InputBase inputRef={ref_preset} onChange={onChange_Name} sx={{ width: '100%', height: '40px', fontSize: 18, paddingLeft: '10px', ..._border_pd, ..._bg_secondary }} />
      </Styled>
    )
  }

  //#endregion

  return display();
}